import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import L from 'leaflet';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './UIMapDynamic.scss';
import UISpinner from '../UISpinner';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

interface IGeoData {
  raw?: {
    addresstype: 'building' | 'house' | 'town' | 'village' | 'city' | 'country';
    type: 'building' | 'house' | 'town' | 'village' | 'city' | 'country';
  };
  label: string;
  x?: number;
  y?: number;
}

export interface UIMapDynamicProps {
  geoData?: IGeoData[];
  className?: string;
}

const baseClass = 'UIMapDynamic';

// Дефолтна позиція - центр Канади (Ottawa)
const DEFAULT_CENTER: [number, number] = [45.4215, -75.6972];
const DEFAULT_ZOOM = 5;

const UIMapDynamic: React.FC<UIMapDynamicProps> = ({
                                                     geoData = [],
                                                     className = '',
                                                   }) => {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ініціалізація Leaflet іконок
  useEffect(() => {
    try {
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: markerIcon2x,
        iconUrl: markerIcon,
        shadowUrl: markerShadow,
      });
      setMapReady(true);
      setError(null);
    } catch (err) {
      console.error('Failed to initialize Leaflet:', err);
      setError('Failed to load map resources');
    }
  }, []);

  // Фільтрація маркерів - тільки building та house з валідними координатами
  const filteredMarkers = geoData.filter((item) => {
    if (item.y == null || item.x == null) {
      return false;
    }
    if (!item.raw) {
      return true;
    }
    const arr = ['building', 'house'];
    const isAddressType = arr.some((i) => item.raw?.addresstype === i);
    const isType = arr.some((i) => item.raw?.type === i);
    return isAddressType || isType;
  });

  // Оновлення виду карти при зміні даних
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !mapReady) {
      return;
    }

    if (filteredMarkers.length === 0) {
      // Повертаємось до дефолтної позиції
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }

    const validCoords = filteredMarkers.map((item) =>
      L.latLng(item.y!, item.x!)
    );

    if (validCoords.length === 1) {
      // Один маркер - центруємо на ньому
      map.setView(validCoords[0], 15);
    } else if (validCoords.length > 1) {
      // Кілька маркерів - показуємо всі
      const bounds = L.latLngBounds(validCoords);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [filteredMarkers, mapReady]);

  // ResizeObserver для коректного відображення при зміні розмірів
  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;

    if (!map || !container || !mapReady) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapReady]);

  const handleMapReady = () => {
    const map = mapRef.current;
    if (!map) return;

    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    if (filteredMarkers.length > 0) {
      const validCoords = filteredMarkers.map((item) =>
        L.latLng(item.y!, item.x!)
      );

      if (validCoords.length === 1) {
        map.setView(validCoords[0], 15);
      } else if (validCoords.length > 1) {
        const bounds = L.latLngBounds(validCoords);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    } else {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }
  };

  if (error) {
    return (
      <div className={clsx(baseClass, className)} ref={containerRef}>
        <div className={`${baseClass}__error`}>
          <p>Failed to load map: {error}</p>
        </div>
      </div>
    );
  }

  if (!mapReady) {
    return (
      <div className={clsx(baseClass, className)} ref={containerRef}>
        <div className={`${baseClass}__placeholder`}>
          <UISpinner />
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(baseClass, className)} ref={containerRef}>
      <MapContainer
        className={`${baseClass}__map-component`}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        ref={mapRef}
        whenReady={handleMapReady}
        scrollWheelZoom={true}
        dragging={true}
        touchZoom={true}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <ZoomControl position="topleft" />
        {filteredMarkers.map((geoDataItem, index) => (
          <Marker
            key={index}
            position={{ lat: geoDataItem.y!, lng: geoDataItem.x! }}
          >
            <Popup className={`${baseClass}__map-component--popup`}>
              {geoDataItem.label}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default UIMapDynamic;